import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import type { ModalProps as AntDModalProps } from 'antd';
import { Modal as AntDModal } from 'antd';

import type { Theme } from '../../theme';
import type { ButtonProps } from '../Button';
import { Button } from '../Button';
import { DesignSystemAntDConfigProvider, getAnimationCss, RestoreAntDDefaultClsPrefix } from '../DesignSystemProvider';
import { useDesignSystemFlags, useDesignSystemTheme } from '../Hooks';
import { CloseIcon, ErrorBorderIcon } from '../Icon';
import type { DangerouslySetAntdProps, HTMLDataAttributes } from '../types';

export interface ModalProps extends HTMLDataAttributes, DangerouslySetAntdProps<AntDModalProps> {
  /** Whether or not the modal is currently open. Use together with onOk and onCancel to control the modal state. */
  visible?: AntDModalProps['visible'];

  /** Function called when the primary button is clicked */
  onOk?: AntDModalProps['onOk'];

  /** Function called when the secondary button is clicked */
  onCancel?: AntDModalProps['onCancel'];

  /** Title displayed at the top of the modal */
  title?: AntDModalProps['title'];

  /** Contents displayed in the body of the modal */
  children?: React.ReactNode;

  /** A custom JSX element to render in place of the default footer */
  footer?: AntDModalProps['footer'];

  /** Sets the horizontal size according to the size presets */
  size?: 'normal' | 'wide';

  /** Defines the modal height either by both its content and maximum height (dynamic) or by its maximum height only (maxed_out) */
  verticalSizing?: 'dynamic' | 'maxed_out';

  /** When set to true, the confirm button will show a loading indicator */
  confirmLoading?: AntDModalProps['confirmLoading'];

  /** Function called after the modal has finished closing */
  afterClose?: AntDModalProps['afterClose'];

  /** Text of the primary button */
  okText?: AntDModalProps['okText'];

  /** Text of the secondary button */
  cancelText?: AntDModalProps['cancelText'];

  /** Set to true to force the modal to render */
  forceRender?: AntDModalProps['forceRender'];

  /** When true, the modal content will be destroyed when closed, rather than just visually hidden. */
  destroyOnClose?: AntDModalProps['destroyOnClose'];

  /** A CSS class name to apply to the modal wrapper */
  wrapClassName?: AntDModalProps['wrapClassName'];

  /** A CSS class name to apply to the modal */
  className?: AntDModalProps['className'];

  /** A function that returns the element into which the modal will be rendered */
  getContainer?: AntDModalProps['getContainer'];

  /**
   * Allows setting the CSS z-index of the modal.
   * Can be used to work around ordering issues when the modal is not overlaying other elements correctly
   */
  zIndex?: AntDModalProps['zIndex'];

  /** The OK button props */
  okButtonProps?: ButtonProps;

  /** The cancel button props */
  cancelButtonProps?: ButtonProps;

  /** Custom modal content render function. While custom rendering is discouraged in general, this prop can be used to wrap the modal contents in a React context. */
  modalRender?: (node: React.ReactNode) => React.ReactNode;
}

const SIZE_PRESETS = {
  normal: 640,
  wide: 880,
};

const getModalEmotionStyles = (args: {
  theme: Theme;
  clsPrefix: string;
  hasFooter: boolean;
  maxedOutHeight: boolean;
}): SerializedStyles => {
  const { theme, clsPrefix, hasFooter = true, maxedOutHeight } = args;

  const classNameClose = `.${clsPrefix}-modal-close`;
  const classNameCloseX = `.${clsPrefix}-modal-close-x`;
  const classNameTitle = `.${clsPrefix}-modal-title`;
  const classNameContent = `.${clsPrefix}-modal-content`;
  const classNameBody = `.${clsPrefix}-modal-body`;
  const classNameHeader = `.${clsPrefix}-modal-header`;
  const classNameFooter = `.${clsPrefix}-modal-footer`;
  const classNameButton = `.${clsPrefix}-btn`;
  const classNameDropdownTrigger = `.${clsPrefix}-dropdown-trigger`;

  const MODAL_PADDING = theme.spacing.lg;
  const BUTTON_SIZE = theme.general.heightSm;
  // Needed for moving some of the padding from the header and footer to the content to avoid a scrollbar from appearing
  // when the content has some interior components that reach the limits of the content div
  // 8px is an arbitrary value, it still leaves enough padding for the header and footer too to avoid the same problem
  // from occurring there too
  const CONTENT_BUFFER = 8;

  const modalMaxHeight = '90vh';
  const headerHeight = 64;
  const footerHeight = hasFooter ? 52 : 0;
  const bodyMaxHeight = `calc(${modalMaxHeight} - ${headerHeight}px - ${footerHeight}px - ${MODAL_PADDING}px)`;

  return css({
    [classNameHeader]: {
      paddingTop: theme.spacing.md,
      paddingLeft: theme.spacing.lg,
      paddingRight: theme.spacing.md,
      paddingBottom: theme.spacing.md,
    },
    [classNameFooter]: {
      height: footerHeight,
      paddingTop: theme.spacing.lg - CONTENT_BUFFER,
      paddingLeft: MODAL_PADDING,
      paddingRight: MODAL_PADDING,
      marginTop: 'auto',

      [`${classNameButton} + ${classNameButton}:not(${classNameDropdownTrigger})`]: {
        marginLeft: theme.spacing.sm,
      },
    },
    [classNameCloseX]: {
      fontSize: theme.general.iconSize,
      height: BUTTON_SIZE,
      width: BUTTON_SIZE,
      lineHeight: 'normal',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    [classNameClose]: {
      height: BUTTON_SIZE,
      width: BUTTON_SIZE,
      // Note: Ant has the close button absolutely positioned, rather than in a flex container with the title.
      // This magic number is eyeballed to get the close X to align with the title text.
      margin: '16px 16px 0 0',
      borderRadius: theme.borders.borderRadiusMd,

      backgroundColor: theme.colors.actionDefaultBackgroundDefault,
      borderColor: theme.colors.actionDefaultBackgroundDefault,
      color: theme.colors.actionDefaultTextDefault,

      '&:hover': {
        backgroundColor: theme.colors.actionDefaultBackgroundHover,
        borderColor: theme.colors.actionDefaultBackgroundHover,
        color: theme.colors.actionDefaultTextHover,
      },

      '&:active': {
        backgroundColor: theme.colors.actionDefaultBackgroundPress,
        borderColor: theme.colors.actionDefaultBackgroundPress,
        color: theme.colors.actionDefaultTextPress,
      },

      '&:focus-visible': {
        outlineStyle: 'solid',
        outlineWidth: '2px',
        outlineOffset: '1px',
        outlineColor: theme.colors.primary,
      },
    },
    [classNameTitle]: {
      fontSize: theme.typography.fontSizeXl,
      lineHeight: theme.typography.lineHeightXl,
      fontWeight: theme.typography.typographyBoldFontWeight,
      paddingRight: MODAL_PADDING,
      minHeight: headerHeight / 2,
      display: 'flex',
      alignItems: 'center',
    },
    [classNameContent]: {
      maxHeight: modalMaxHeight,
      height: maxedOutHeight ? modalMaxHeight : '',
      overflow: 'hidden',
      paddingBottom: MODAL_PADDING,
      display: 'flex',
      flexDirection: 'column',
    },
    [classNameBody]: {
      overflowY: 'auto',
      maxHeight: bodyMaxHeight,
      paddingLeft: MODAL_PADDING,
      paddingRight: MODAL_PADDING,
      paddingTop: CONTENT_BUFFER,
      paddingBottom: CONTENT_BUFFER,

      ...(theme.themeName === 'default'
        ? {
            // Achieves an inner shadow on the content, but only when there is more left to scroll. When the content fits
            // in the container without scrolling, no shadow will be shown.
            // Taken from: https://css-tricks.com/scroll-shadows-with-javascript/
            background: `linear-gradient(
              white 30%,
              rgba(255, 255, 255, 0)
            ) center top,

            linear-gradient(
              rgba(255, 255, 255, 0),
              white 70%
            ) center bottom,

            radial-gradient(
              farthest-side at 50% 0,
              rgba(0, 0, 0, 0.2),
              rgba(0, 0, 0, 0)
            ) center top,

            radial-gradient(
              farthest-side at 50% 100%,
              rgba(0, 0, 0, 0.2),
              rgba(0, 0, 0, 0)
            ) center bottom`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 40px, 100% 40px, 100% 14px, 100% 14px',
            backgroundAttachment: 'local, local, scroll, scroll',
          }
        : {}),
    },

    ...getAnimationCss(theme.options.enableAnimation),
  });
};

export function Modal({
  okButtonProps,
  cancelButtonProps,
  dangerouslySetAntdProps,
  children,
  title,
  footer,
  size = 'normal',
  verticalSizing = 'dynamic',
  ...props
}: ModalProps): JSX.Element {
  const { theme, classNamePrefix } = useDesignSystemTheme();

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onCancel?.(e);
  };

  const handleOk = (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onOk?.(e);
  };

  /**
   * Render default footer with our buttons. Copied from AntD.
   */
  const renderDefaultFooter = () => {
    const { okText, cancelText, confirmLoading } = props;
    return (
      <>
        {cancelText && (
          <Button onClick={handleCancel} {...cancelButtonProps}>
            {cancelText}
          </Button>
        )}
        {okText && (
          <Button loading={confirmLoading} onClick={handleOk} type="primary" {...okButtonProps}>
            {okText}
          </Button>
        )}
      </>
    );
  };

  return (
    <DesignSystemAntDConfigProvider>
      <AntDModal
        css={getModalEmotionStyles({
          theme,
          clsPrefix: classNamePrefix,
          hasFooter: footer !== null,
          maxedOutHeight: verticalSizing === 'maxed_out',
        })}
        title={<RestoreAntDDefaultClsPrefix>{title}</RestoreAntDDefaultClsPrefix>}
        footer={
          <RestoreAntDDefaultClsPrefix>
            {footer === undefined ? renderDefaultFooter() : footer}
          </RestoreAntDDefaultClsPrefix>
        }
        width={size ? SIZE_PRESETS[size] : undefined}
        closeIcon={<CloseIcon />}
        centered
        zIndex={theme.options.zIndexBase}
        {...props}
        {...dangerouslySetAntdProps}
      >
        <RestoreAntDDefaultClsPrefix>{children}</RestoreAntDDefaultClsPrefix>
      </AntDModal>
    </DesignSystemAntDConfigProvider>
  );
}

export function DangerModal(props: Omit<ModalProps, 'footer'>): JSX.Element {
  const { theme } = useDesignSystemTheme();
  const { USE_NEW_ICONS: useNewIcons } = useDesignSystemFlags();

  const { title, onCancel, onOk, cancelText, okText, okButtonProps, cancelButtonProps, ...restProps } = props;
  const iconSize = useNewIcons ? 18 : 20;
  const iconFontSize = useNewIcons ? 18 : 22;

  const titleComp = (
    <div css={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <ErrorBorderIcon
        css={{
          color: theme.colors.textValidationDanger,
          left: 2,
          height: iconSize,
          width: iconSize,
          fontSize: iconFontSize,
        }}
      />
      <div css={{ paddingLeft: 6 }}>{title}</div>
    </div>
  );

  return (
    <Modal
      title={titleComp}
      footer={[
        <Button key="cancel" onClick={onCancel} {...cancelButtonProps}>
          {cancelText || 'Cancel'}
        </Button>,

        <Button key="discard" type="primary" danger onClick={onOk} {...okButtonProps}>
          {okText || 'Delete'}
        </Button>,
      ]}
      onOk={onOk}
      onCancel={onCancel}
      {...restProps}
    />
  );
}
